/**
 * Timezone utilities for NEIMUS360.
 *
 * All timestamps in the database are stored in UTC.
 * This module converts them to the user's configured timezone
 * for display in charts, cards, and timestamps.
 */

// Cache the timezone so we don't fetch it on every call
let _cachedTimezone: string | null = null;
let _cacheTimestamp = 0;
const CACHE_TTL_MS = 60_000; // Re-fetch every 60 seconds

/**
 * Get the user's configured timezone.
 * Falls back to browser timezone if not set.
 */
export function getUserTimezone(): string {
  // Check cache
  if (_cachedTimezone && Date.now() - _cacheTimestamp < CACHE_TTL_MS) {
    return _cachedTimezone;
  }
  return _cachedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Set the cached timezone (called after fetching from API).
 */
export function setUserTimezone(tz: string) {
  _cachedTimezone = tz;
  _cacheTimestamp = Date.now();
}

/**
 * Format a UTC timestamp or Date to a localized time string in the user's timezone.
 * Example: "2:30 PM" or "14:30"
 */
export function formatTime(input: string | Date, use24h = false): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  const tz = getUserTimezone();
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: !use24h,
    timeZone: tz,
  });
}

/**
 * Format a UTC timestamp to a short date string in the user's timezone.
 * Example: "Jan 5" or "Mon, Jan 5"
 */
export function formatDate(input: string | Date, includeWeekday = false): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  const tz = getUserTimezone();
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  };
  if (includeWeekday) {
    opts.weekday = 'short';
  }
  return date.toLocaleDateString([], opts);
}

/**
 * Format a UTC timestamp to a full date+time string in the user's timezone.
 * Example: "Jan 5, 2026, 2:30 PM"
 */
export function formatDateTime(input: string | Date): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  const tz = getUserTimezone();
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
  });
}

/**
 * Format a UTC timestamp for chart X-axis labels based on time range.
 * - 24h: "2:30 PM"
 * - 7d: "Mon"
 * - 30d: "Jan 5"
 */
export function formatChartLabel(input: string | Date, range: '24h' | '7d' | '30d' = '24h'): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  const tz = getUserTimezone();

  switch (range) {
    case '24h':
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: tz,
      });
    case '7d':
      return date.toLocaleDateString([], {
        weekday: 'short',
        timeZone: tz,
      });
    case '30d':
      return date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        timeZone: tz,
      });
    default:
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: tz,
      });
  }
}

/**
 * Format relative time ("Just now", "5m ago", "2h ago", "3d ago").
 * This doesn't need timezone conversion since it's relative.
 */
export function formatRelativeTime(input: string | Date): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(date);
}

/**
 * Get the timezone abbreviation for display (e.g., "EST", "PST", "UTC").
 */
export function getTimezoneAbbr(): string {
  const tz = getUserTimezone();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value || tz;
}

/**
 * Patch Date.prototype locale methods to inject the user's timezone
 * into ALL toLocaleString / toLocaleTimeString / toLocaleDateString calls
 * that don't already specify a timezone.
 *
 * This ensures every timestamp across the entire frontend uses the
 * configured timezone without modifying individual components.
 *
 * Call this ONCE at app startup (main.tsx).
 */
export function patchDatePrototypeWithTimezone() {
  const origToLocaleString = Date.prototype.toLocaleString;
  const origToLocaleTimeString = Date.prototype.toLocaleTimeString;
  const origToLocaleDateString = Date.prototype.toLocaleDateString;

  Date.prototype.toLocaleString = function (
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions
  ): string {
    const tz = getUserTimezone();
    const opts = { timeZone: tz, ...options };
    return origToLocaleString.call(this, locales, opts);
  };

  Date.prototype.toLocaleTimeString = function (
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions
  ): string {
    const tz = getUserTimezone();
    const opts = { timeZone: tz, ...options };
    return origToLocaleTimeString.call(this, locales, opts);
  };

  Date.prototype.toLocaleDateString = function (
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions
  ): string {
    const tz = getUserTimezone();
    const opts = { timeZone: tz, ...options };
    return origToLocaleDateString.call(this, locales, opts);
  };
}
